import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type {
  CardsListItem,
  EditCardRequest,
  ListCardsResponseWithProjectName,
} from "tng-address-collection-shared/types/apiTypes.ts";
import {
  getSenderDisplayName,
  getSenderSignature,
  optimisticSignatureFields,
  withSenderSignature,
} from "./getSenderSignature.ts";
import { queryKeys } from "./queryKeys.ts";

export const useCreateCardProjectCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (
      newCard: EditCardRequest,
      responsibleProjectId: string,
      userUidNumber?: number,
    ) => {
      queryClient.setQueryData<ListCardsResponseWithProjectName>(
        queryKeys.cardsInProject(newCard.projectId),
        (old) => {
          const shared = newCard.projectId !== responsibleProjectId;
          const senderSignature = getSenderSignature(
            queryClient,
            newCard.senderId,
          );
          const signatureFields = optimisticSignatureFields(
            withSenderSignature([], senderSignature),
            old?.hiddenFields,
            userUidNumber,
          );
          const senderDisplayName = getSenderDisplayName(
            queryClient,
            newCard.senderId,
          );
          const tempCard: CardsListItem = {
            id: newCard.id,
            projectId: newCard.projectId,
            responsibleProjectId,
            shared,
            firstName: newCard.recipient.firstName,
            lastName: newCard.recipient.lastName,
            companyName: newCard.recipient.companyName,
            departmentName: newCard.departmentName,
            senderId: newCard.senderId,
            senderDisplayName,
            manualDispatch: newCard.manualDispatch,
            email: newCard.recipient.email,
            comment: newCard.comment,
            hasContactDetailsWarning: false,
            senderUnknown: false,
            signatures: signatureFields.signatures,
            numberOfSignatures: signatureFields.numberOfSignatures,
            isSignedByUser: signatureFields.isSignedByUser,
            language: newCard.recipient.preferredLanguage,
            title: newCard.recipient.title,
            salutation: newCard.recipient.salutationKind,
            receivesGift: newCard.receivesGift,
            formality: newCard.formality,
          };
          const itemsWithoutNewCard = (old?.items || []).filter(
            (item) => item.id !== tempCard.id,
          );
          const updatedResponse: ListCardsResponseWithProjectName = {
            projectDisplayName: old?.projectDisplayName || "",
            items: [...itemsWithoutNewCard, tempCard],
            hiddenFields: old?.hiddenFields || [],
            projectShepherds: old?.projectShepherds || {
              groups: [],
              individuals: [],
            },
            projectInfoBox: old?.projectInfoBox,
            hasPermissions: old?.hasPermissions ?? true,
          };
          return updatedResponse;
        },
      );
    },
    [queryClient],
  );
};
